import React from "react";
import { Workflow } from "lucide-react";
import { motion } from "framer-motion";
import { steps } from "../constants/steps";

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.15, delayChildren: 0.1 } },
};
const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const HowItWorksSection = () => (
  <section id="how-it-works" className="py-24 px-6 overflow-hidden" style={{ background: "#0F172A" }}>
    <div className="max-w-6xl mx-auto">
      {/* Top badge */}
      <motion.div
        className="flex justify-center mb-6"
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.4 }}
      >
        <span
          className="inline-flex items-center gap-1.5 border border-gray-600 text-gray-300 text-xs font-semibold px-4 py-1.5 rounded-full"
          style={{ background: "rgba(255,255,255,0.05)" }}
        >
          <Workflow className="w-3 h-3" />
          Smart Workflow
        </span>
      </motion.div>

      <motion.div
        className="text-center mb-16"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      >
        <h2 className="text-3xl font-extrabold text-white mb-3">How It Works</h2>
        <p className="text-slate-400 max-w-xl mx-auto">
          From policy text to actionable insights — and continuous monitoring — all in one intelligent platform.
        </p>
      </motion.div>

      <motion.div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
      >
        {steps.map((s, i) => (
          <motion.div
            key={s.title}
            variants={cardVariants}
            whileHover={{ y: -8, scale: 1.02 }}
            transition={{ type: "spring", stiffness: 260, damping: 18 }}
            className={`group rounded-2xl p-6 flex flex-col gap-4 border cursor-default
              transition-colors duration-300 ${s.border} ${s.hoverBorder} ${s.hoverBg}`}
            style={{ background: s.cardBg }}
          >
            {/* Step number */}
            <div className="flex items-center justify-between">
              <motion.div
                className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:${s.iconBgHover}`}
                style={{ background: s.iconBg }}
                whileHover={{ rotate: 10, scale: 1.15 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <s.Icon className={`w-6 h-6 ${s.highlight ? s.iconActive : "text-slate-400"} group-hover:${s.iconActive} transition-colors duration-300`} />
              </motion.div>
              <span className="text-3xl font-black text-white/20 select-none">{String(i + 1).padStart(2, "0")}</span>
            </div>
            <div>
              <p className={`text-xs font-bold tracking-widest mb-2 ${s.labelColor}`}>{s.label}</p>
              <h3 className="font-bold text-white mb-2" dangerouslySetInnerHTML={{ __html: s.title }} />
              <p className="text-sm text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors duration-300">{s.desc}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

export default HowItWorksSection;
